/**
 * Admin Promotions — Painel de campanhas promocionais (cupons)
 * Abas: Campanhas, Criar/Editar, Resgates, Analytics
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { Button, Input, Select, SkeletonBar, TabBar, MetricCard, Modal, Input as UiInput, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

// ── Ícones ──
const Icons = {
  plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  copy: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>,
  pause: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  play: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  x: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  eye: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
};

const TABS = [
  { id: 'campaigns', label: 'Campanhas' },
  { id: 'create', label: 'Criar Campanha' },
  { id: 'redemptions', label: 'Resgates' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'affiliates', label: 'Afiliados' },
];

// ── Estilos (dark theme padrão) ──
const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' } as any,
  title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 24 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 } as any,
  tabBar: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.bgHover}`, marginBottom: 24, overflowX: 'auto' as any } as any,
  tab: (active: boolean) => ({
    padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
    color: active ? T.primary : T.textSecondary, borderBottom: active ? `2px solid ${T.primary}` : '2px solid transparent',
    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' as any, transition: 'all 0.15s',
  }),
  input: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any } as any,
  select: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any } as any,
  textarea: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', minHeight: 60, resize: 'vertical' as any, boxSizing: 'border-box' as any } as any,
  btn: (variant: 'primary' | 'danger' | 'ghost' | 'success') => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
    ...(variant === 'primary' ? { background: T.primary, color: T.text } :
       variant === 'danger' ? { background: T.danger, color: T.text } :
       variant === 'success' ? { background: T.success, color: T.text } :
       { background: T.border, color: T.textSecondary }),
  }),
  table: { width: '100%', borderCollapse: 'collapse' as any, fontSize: 13 } as any,
  th: { textAlign: 'left' as any, padding: '10px 12px', color: T.textSecondary, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as any, letterSpacing: '0.5px', borderBottom: `1px solid ${T.border}` } as any,
  td: { padding: '12px 12px', color: T.text, borderBottom: `1px solid ${T.border}` } as any,
  badge: (color: string) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: `${color}20`, color: color,
  }),
  label: { fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 4, display: 'block' } as any,
  formGroup: { marginBottom: 16 } as any,
};

// Cores por tipo/status
const TYPE_COLORS: Record<string, string> = { trial: T.primary, credits: T.success, discount_percent: T.warning, gift: T.purple };
const TYPE_LABELS: Record<string, string> = { trial: 'Trial', credits: 'Créditos', discount_percent: 'Desconto %', gift: 'Presente' };
const STATUS_COLORS: Record<string, string> = { active: T.success, paused: T.warning, expired: T.danger, archived: T.textMuted };
const STATUS_LABELS: Record<string, string> = { active: 'Ativa', paused: 'Pausada', expired: 'Expirada', archived: 'Arquivada' };

// Cores por fonte de tráfego
const SOURCE_COLORS: Record<string, string> = {
  google: T.success, instagram: '#E1306C', facebook: '#1877F2', tiktok: T.bg,
  youtube: '#FF0000', twitter: '#1DA1F2', email: T.warning, direct: T.textMuted, other: T.purple,
};
const SOURCE_LABELS: Record<string, string> = {
  google: 'Google', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok',
  youtube: 'YouTube', twitter: 'Twitter/X', email: 'Email', direct: 'Direto', other: 'Outro',
};

// Cores por ação
const ACTION_COLORS: Record<string, string> = {
  visit: T.textMuted, signup: T.primary, redeem: T.success, rejected: T.danger,
};
const ACTION_LABELS: Record<string, string> = {
  visit: 'Visita', signup: 'Cadastro', redeem: 'Resgate', rejected: 'Rejeitado',
};

// Fontes de tráfego disponíveis para filtro
// ── Templates de Banner ──
const BEFORE_LOGIN_TEMPLATES = [
  { id: 'code_activated', name: 'Código Ativado', title: 'Seu código promocional foi ativado!', message: 'O código {{CODE}} está reservado para você. Faça login para garantir seu benefício!', bg_color: T.success, description: 'Mostra que o código promo foi reconhecido' },
  { id: 'welcome_offer', name: 'Oferta de Boas-Vindas', title: 'Oferta especial para você!', message: 'Você foi convidado com um código exclusivo. Entre agora e aproveite!', bg_color: T.primary, description: 'Convite genérico para visitantes' },
  { id: 'limited_time', name: 'Tempo Limitado', title: 'Oferta por tempo limitado!', message: 'Esta promoção acaba em breve. Não perca sua chance!', bg_color: T.danger, description: 'Urgência para converter visitante' },
  { id: 'exclusive_invite', name: '👑 Convite Exclusivo', title: '👑 Você recebeu um convite exclusivo', message: 'Poucos foram selecionados para esta oferta. Faça login para ativar.', bg_color: T.purple, description: 'Sensação de exclusividade' },
];

const AFTER_LOGIN_TEMPLATES = [
  { id: 'credits_earned', name: 'Créditos Ganhos', title: 'Parabéns! Você ganhou créditos!', message: 'Seus créditos já foram adicionados à sua conta. Comece a usar agora!', bg_color: T.success, description: 'Confirma créditos adicionados' },
  { id: 'trial_activated', name: 'Trial Ativado', title: 'Seu período de teste foi ativado!', message: 'Aproveite todos os recursos premium durante o período de teste.', bg_color: T.accent, description: 'Confirma ativação do trial' },
  { id: 'discount_applied', name: '🏷️ Desconto Aplicado', title: '🏷️ Seu desconto está ativo!', message: 'O desconto foi aplicado automaticamente. Faça upgrade agora com preço especial!', bg_color: T.warning, description: 'Confirma desconto ativado' },
  { id: 'welcome_bonus', name: 'Bônus de Boas-Vindas', title: 'Seu bônus está esperando!', message: 'Obrigado por se juntar a nós! Confira o que preparamos para você.', bg_color: T.danger, description: 'Boas-vindas após resgate' },
  { id: 'gift_pending', name: 'Presente Disponível', title: 'Você tem um presente!', message: 'Alguém te enviou um presente especial. Resgate agora!', bg_color: T.purple, description: 'Notifica presente pendente' },
];

const COUNTDOWN_TEMPLATES = [
  { id: 'coming_soon', name: 'Em Breve', title: 'Promoção chegando!', message: 'Prepare-se para uma oferta incrível. Fique ligado!', bg_color: T.purple, text_color: T.text },
  { id: 'black_friday', name: '🖤 Black Friday', title: '🖤 Black Friday chegando!', message: 'A maior promoção do ano está quase aí.', bg_color: T.bg, text_color: T.warning },
  { id: 'launch_day', name: 'Dia de Lançamento', title: 'Lançamento em...', message: 'Algo incrível está por vir. Marque na agenda!', bg_color: '#1E40AF', text_color: T.text },
];

const EXPIRED_TEMPLATES = [
  { id: 'ended', name: '⌛ Encerrada', title: '⌛ Esta promoção já encerrou', message: 'Fique de olho para as próximas ofertas!', bg_color: T.textMuted },
  { id: 'sold_out', name: 'Esgotado', title: 'Vagas esgotadas!', message: 'Esta promoção foi um sucesso e todas as vagas foram preenchidas.', bg_color: T.danger },
  { id: 'comeback', name: 'Voltaremos', title: 'Em breve teremos novidades!', message: 'Essa promoção acabou mas estamos preparando algo especial.', bg_color: '#4F46E5' },
];

// ── Componente TemplatePicker ──
function TemplatePicker({ templates, selectedId, onSelect, codeValue }: { templates: any[]; selectedId: string; onSelect: (t: any) => void; codeValue: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8, display: 'block' }}>Escolha um modelo:</span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {templates.map((t: any) => (
          <Button variant="ghost"
            key={t.id}
            type="button"
            onClick={() => onSelect(t)}
            style={{
              padding: '10px 14px', borderRadius: 10, cursor: 'pointer', border: selectedId === t.id ? '2px solid #fff' : `1px solid ${T.borderHover}`,
              background: selectedId === t.id ? `${t.bg_color}40` : T.bgCard,
              color: selectedId === t.id ? '#fff' : T.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'center' as any,
              minWidth: 100, transition: 'all 0.15s',
              boxShadow: selectedId === t.id ? `0 0 12px ${t.bg_color}60` : 'none',
            }}
          >
            <div style={{ fontSize: 14, marginBottom: 2 }}>{t.name}</div>
            {t.description && <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>{t.description}</div>}
            <div style={{ width: 24, height: 4, borderRadius: 2, background: t.bg_color, margin: '6px auto 0' }} />
          </Button>
        ))}
      </div>
    </div>
  );
}

// Substituir {{CODE}} por valor real no preview
function resolveVars(text: string, code: string): string {
  if (!text) return text;
  return text.replace(/\{\{CODE\}\}/g, code || 'PROMO-XXXXXX');
}

const TRAFFIC_SOURCES = [
  { id: 'google', label: 'Google' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'email', label: 'Email' },
  { id: 'direct', label: 'Direto' },
  { id: 'other', label: 'Outro' },
];

// ── Componente BarChart (copiado do AdminChatHub) ──
function BarChart({ data, height = 120, color = T.primary, formatValue }: { data: { label: string; value: number }[]; height?: number; color?: string; formatValue?: (v: number) => string }) {
  if (!data.length) return <div style={{ color: T.textMuted, fontSize: 13 }}>Sem dados</div>;
  const max = Math.max(...data.map(d => d.value), 1);
  const barW = Math.max(Math.floor((100 / data.length) - 1), 2);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height, width: '100%' }}>
      {data.map((d, i) => {
        const h = Math.max((d.value / max) * (height - 20), 2);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div title={`${d.label}: ${formatValue ? formatValue(d.value) : d.value}`}
              style={{ width: '100%', maxWidth: 40, height: h, background: color, borderRadius: '4px 4px 0 0', minWidth: 4, transition: 'height 0.3s' }} />
            <span style={{ fontSize: 9, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 40 }}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Componente de Preview do Banner ──
function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm)(\?.*)?$/i.test(url);
}

function SlotsIndicator({ max, current, textColor }: { max: number; current: number; textColor?: string }) {
  if (!max || max <= 0) return null;
  const remaining = Math.max(0, max - current);
  const pct = remaining / max;
  const urgentColor = pct <= 0.2 ? T.danger : pct <= 0.5 ? T.warning : (textColor || '#fff');
  return (
    <div style={{ fontSize: 13, fontWeight: 700, color: urgentColor, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
      {pct <= 0.2 ? '' : ''} {remaining === 0 ? 'Esgotado!' : `Restam ${remaining} de ${max} vagas`}
      {pct <= 0.2 && remaining > 0 && <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}> — Corra!</span>}
    </div>
  );
}

function BannerPreview({ title, message, bgColor, imageUrl, maxRedemptions, currentRedemptions }: { title: string; message: string; bgColor: string; imageUrl: string; maxRedemptions?: number; currentRedemptions?: number }) {
  if (!title && !message) return null;
  const isVideo = imageUrl && isVideoUrl(imageUrl);
  return (
    <div style={{ marginTop: 12 }}>
      <span style={s.label}>Preview do Banner</span>
      <div style={{
        background: bgColor || T.primary, borderRadius: 12, padding: 24, color: T.text,
        display: 'flex', alignItems: 'center', gap: 16, maxWidth: 500,
      }}>
        {imageUrl && (isVideo
          ? <video src={imageUrl} style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} autoPlay muted loop playsInline />
          : <img src={imageUrl} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />
        )}
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{title || 'Título do banner'}</div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>{message || 'Mensagem do banner'}</div>
          <SlotsIndicator max={maxRedemptions || 0} current={currentRedemptions || 0} />
        </div>
      </div>
    </div>
  );
}

// ── Componente CountdownPreview (admin) ──
function CountdownPreview({ title, message, bgColor, textColor, imageUrl, startsAt, maxRedemptions, currentRedemptions }: {
  title: string; message: string; bgColor: string; textColor: string; imageUrl: string; startsAt: string; maxRedemptions?: number; currentRedemptions?: number;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = startsAt ? new Date(startsAt).getTime() : 0;
  const diff = Math.max(0, target - now);
  const dias = Math.floor(diff / 86400000);
  const hrs = Math.floor((diff % 86400000) / 3600000);
  const min = Math.floor((diff % 3600000) / 60000);
  const seg = Math.floor((diff % 60000) / 1000);

  const pad = (n: number) => String(n).padStart(2, '0');
  const isVideo = imageUrl && isVideoUrl(imageUrl);

  return (
    <div style={{ marginTop: 12 }}>
      <span style={s.label}>Preview do Countdown</span>
      <div style={{
        background: bgColor || T.purple, borderRadius: 12, padding: 24, color: textColor || '#fff',
        display: 'flex', alignItems: 'center', gap: 16, maxWidth: 520,
      }}>
        {imageUrl && (isVideo
          ? <video src={imageUrl} style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} autoPlay muted loop playsInline />
          : <img src={imageUrl} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{title || 'Promoção chegando!'}</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {[
              { v: pad(dias), l: 'Dias' },
              { v: pad(hrs), l: 'Hrs' },
              { v: pad(min), l: 'Min' },
              { v: pad(seg), l: 'Seg' },
            ].map((item) => (
              <div key={item.l} style={{ textAlign: 'center' }}>
                <div style={{
                  background: T.blackAlpha30, borderRadius: 8, padding: '6px 10px',
                  fontSize: 20, fontWeight: 700, fontFamily: 'monospace', minWidth: 40,
                }}>{item.v}</div>
                <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>{item.l}</div>
              </div>
            ))}
          </div>
          {message && <div style={{ fontSize: 13, opacity: 0.9 }}>{message}</div>}
          <SlotsIndicator max={maxRedemptions || 0} current={currentRedemptions || 0} textColor={textColor} />
        </div>
      </div>
    </div>
  );
}

// ── Formulário de criação/edição ──
function CampaignForm({ promotion, onSave, onCancel }: { promotion?: any; onSave: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    name: promotion?.name || '',
    code: promotion?.code || '',
    description: promotion?.description || '',
    starts_at: promotion?.starts_at ? promotion.starts_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
    ends_at: promotion?.ends_at ? promotion.ends_at.slice(0, 16) : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 16),
    benefit_type: promotion?.benefit_type || 'credits',
    // Benefício
    trial_plan: 'pro',
    trial_days: '7',
    credits_amount: '5.00',
    discount_percent: '30',
    discount_plan: 'pro',
    // Presente
    gift_type: 'credits' as 'credits' | 'trial' | 'discount_percent',
    gift_credits_amount: '10.00',
    gift_trial_plan: 'pro',
    gift_trial_days: '7',
    gift_discount_percent: '30',
    gift_discount_plan: 'pro',
    gift_message: 'Você ganhou um presente especial!',
    // Fonte de tráfego
    any_traffic_source: true,
    allowed_sources: [] as string[],
    // Countdown (pré-lançamento)
    countdown_enabled: promotion?.countdown_enabled ?? 1,
    countdown_title: promotion?.countdown_title || '',
    countdown_message: promotion?.countdown_message || '',
    countdown_bg_color: promotion?.countdown_bg_color || T.purple,
    countdown_text_color: promotion?.countdown_text_color || T.text,
    countdown_image_url: promotion?.countdown_image_url || '',
    // Banner (campos legados mantidos para fallback)
    banner_title: promotion?.banner_title || '',
    banner_message: promotion?.banner_message || '',
    banner_image_url: promotion?.banner_image_url || '',
    banner_bg_color: promotion?.banner_bg_color || T.primary,
    banner_show_count: String(promotion?.banner_show_count ?? 3),
    banner_timing: promotion?.banner_timing || 'after_login',
    // Banner Antes do Login
    banner_before_enabled: promotion?.banner_before_enabled ?? 1,
    banner_before_title: promotion?.banner_before_title || '',
    banner_before_message: promotion?.banner_before_message || '',
    banner_before_image_url: promotion?.banner_before_image_url || '',
    banner_before_bg_color: promotion?.banner_before_bg_color || T.primary,
    banner_before_template: '' as string,
    // Banner Depois do Login
    banner_after_enabled: promotion?.banner_after_enabled ?? 1,
    banner_after_title: promotion?.banner_after_title || '',
    banner_after_message: promotion?.banner_after_message || '',
    banner_after_image_url: promotion?.banner_after_image_url || '',
    banner_after_bg_color: promotion?.banner_after_bg_color || T.success,
    banner_after_template: '' as string,
    // Banner Promoção Encerrada
    banner_expired_enabled: promotion?.banner_expired_enabled ?? 0,
    banner_expired_title: promotion?.banner_expired_title || '',
    banner_expired_message: promotion?.banner_expired_message || '',
    banner_expired_bg_color: promotion?.banner_expired_bg_color || T.textMuted,
    banner_expired_image_url: promotion?.banner_expired_image_url || '',
    banner_expired_template: '' as string,
    // Countdown template
    countdown_template: '' as string,
    max_redemptions: promotion?.max_redemptions ? String(promotion.max_redemptions) : '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Parsear benefit_value e allowed_traffic_sources existentes
  useEffect(() => {
    if (promotion?.benefit_value) {
      try {
        const bv = typeof promotion.benefit_value === 'string' ? JSON.parse(promotion.benefit_value) : promotion.benefit_value;
        if (promotion.benefit_type === 'trial') {
          setForm(f => ({ ...f, trial_plan: bv.plan || 'pro', trial_days: String(bv.days || 7) }));
        } else if (promotion.benefit_type === 'credits') {
          setForm(f => ({ ...f, credits_amount: String(bv.amount || 5) }));
        } else if (promotion.benefit_type === 'discount_percent') {
          setForm(f => ({ ...f, discount_percent: String(bv.percent || 30), discount_plan: bv.plan || 'pro' }));
        } else if (promotion.benefit_type === 'gift') {
          setForm(f => ({
            ...f,
            gift_type: bv.gift_type || 'credits',
            gift_credits_amount: String(bv.gift_credits_amount || 10),
            gift_trial_plan: bv.gift_trial_plan || 'pro',
            gift_trial_days: String(bv.gift_trial_days || 7),
            gift_discount_percent: String(bv.gift_discount_percent || 30),
            gift_discount_plan: bv.gift_discount_plan || 'pro',
            gift_message: bv.gift_message || 'Você ganhou um presente especial!',
          }));
        }
      } catch {}
    }
    // Parsear fontes de tráfego
    if (promotion?.allowed_traffic_sources && promotion.allowed_traffic_sources !== '*') {
      try {
        const sources = JSON.parse(promotion.allowed_traffic_sources);
        if (Array.isArray(sources) && sources.length > 0) {
          setForm(f => ({ ...f, any_traffic_source: false, allowed_sources: sources }));
        }
      } catch {}
    }
  }, []);

  const update = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  const toggleSource = (sourceId: string) => {
    setForm(f => {
      const sources = f.allowed_sources.includes(sourceId)
        ? f.allowed_sources.filter(s => s !== sourceId)
        : [...f.allowed_sources, sourceId];
      return { ...f, allowed_sources: sources };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let benefit_value: any;
      if (form.benefit_type === 'trial') {
        benefit_value = { plan: form.trial_plan, days: parseInt(form.trial_days) };
      } else if (form.benefit_type === 'credits') {
        benefit_value = { amount: parseFloat(form.credits_amount) };
      } else if (form.benefit_type === 'gift') {
        benefit_value = {
          gift_type: form.gift_type,
          gift_message: form.gift_message,
          ...(form.gift_type === 'credits' && { gift_credits_amount: parseFloat(form.gift_credits_amount) }),
          ...(form.gift_type === 'trial' && { gift_trial_plan: form.gift_trial_plan, gift_trial_days: parseInt(form.gift_trial_days) }),
          ...(form.gift_type === 'discount_percent' && { gift_discount_percent: parseInt(form.gift_discount_percent), gift_discount_plan: form.gift_discount_plan }),
        };
      } else {
        benefit_value = { percent: parseInt(form.discount_percent), plan: form.discount_plan };
      }

      const allowedTrafficSources = form.any_traffic_source ? '*' : JSON.stringify(form.allowed_sources);

      const payload = {
        name: form.name,
        code: form.code || undefined,
        description: form.description || undefined,
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        benefit_type: form.benefit_type,
        benefit_value: JSON.stringify(benefit_value),
        allowed_traffic_sources: allowedTrafficSources,
        banner_title: form.banner_title || undefined,
        banner_message: form.banner_message || undefined,
        banner_image_url: form.banner_image_url || undefined,
        banner_bg_color: form.banner_bg_color,
        banner_show_count: parseInt(form.banner_show_count) || 3,
        banner_timing: form.banner_timing,
        // Banner antes do login
        banner_before_enabled: form.banner_before_enabled ? 1 : 0,
        banner_before_title: form.banner_before_title || undefined,
        banner_before_message: form.banner_before_message || undefined,
        banner_before_image_url: form.banner_before_image_url || undefined,
        banner_before_bg_color: form.banner_before_bg_color,
        // Banner depois do login
        banner_after_enabled: form.banner_after_enabled ? 1 : 0,
        banner_after_title: form.banner_after_title || undefined,
        banner_after_message: form.banner_after_message || undefined,
        banner_after_image_url: form.banner_after_image_url || undefined,
        banner_after_bg_color: form.banner_after_bg_color,
        // Banner expirado
        banner_expired_enabled: form.banner_expired_enabled ? 1 : 0,
        banner_expired_title: form.banner_expired_title || undefined,
        banner_expired_message: form.banner_expired_message || undefined,
        banner_expired_bg_color: form.banner_expired_bg_color,
        banner_expired_image_url: form.banner_expired_image_url || undefined,
        // Countdown
        countdown_enabled: form.countdown_enabled ? 1 : 0,
        countdown_title: form.countdown_title || undefined,
        countdown_message: form.countdown_message || undefined,
        countdown_bg_color: form.countdown_bg_color,
        countdown_text_color: form.countdown_text_color,
        countdown_image_url: form.countdown_image_url || undefined,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
      };

      if (promotion?.id) {
        await api.put(`/admin/promotions/${promotion.id}`, payload);
      } else {
        await api.post('/admin/promotions', payload);
      }
      onSave();
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ color: T.text, fontSize: 18, fontWeight: 600, margin: 0 }}>
          {promotion ? 'Editar Campanha' : 'Nova Campanha'}
        </h3>
        <Button variant="ghost" size="xs" onClick={onCancel}>{Icons.x}</Button>
      </div>

      {error && <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBg}`, borderRadius: 8, padding: '8px 12px', color: T.danger, fontSize: 13, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={s.formGroup}>
          <label style={s.label}>Nome da campanha *</label>
          <Input value={form.name} onChange={(v) => update('name', v)} placeholder="Ex: Black Friday 2026" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Código (auto-gerado se vazio)</label>
          <Input value={form.code} onChange={(v) => update('code', v.toUpperCase())} placeholder="PROMO-XXXXXX" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Início *</label>
          <Input type="datetime-local" value={form.starts_at} onChange={(v) => update('starts_at', v)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Fim *</label>
          <Input type="datetime-local" value={form.ends_at} onChange={(v) => update('ends_at', v)} />
        </div>
      </div>

      <div style={s.formGroup}>
        <label style={s.label}>Descrição</label>
        <Textarea style={s.textarea} value={form.description} onInput={(e: any) => update('description', e.target.value)} placeholder="Descrição opcional..." />
      </div>

      {/* Tipo de benefício */}
      <div style={s.formGroup}>
        <label style={s.label}>Tipo de benefício *</label>
        <Select value={form.benefit_type} onChange={(v) => update('benefit_type', v)} options={[{ value: 'credits', label: 'Créditos ($)' }, { value: 'trial', label: 'Trial (dias grátis)' }, { value: 'discount_percent', label: 'Desconto %' }, { value: 'gift', label: 'Presente (resgate manual)' }]} />
      </div>

      {/* Valor do benefício (dinâmico) */}
      {form.benefit_type === 'credits' && (
        <div style={s.formGroup}>
          <label style={s.label}>Valor em créditos ($)</label>
          <input style={s.input} type="number" step="0.01" value={form.credits_amount} onInput={(e: any) => update('credits_amount', e.target.value)} />
        </div>
      )}
      {form.benefit_type === 'trial' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.formGroup}>
            <label style={s.label}>Plano</label>
            <Select value={form.trial_plan} onChange={(v) => update('trial_plan', v)} options={[{ value: 'starter', label: 'Starter' }, { value: 'pro', label: 'Pro' }, { value: 'enterprise', label: 'Enterprise' }]} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Dias de trial</label>
            <Input type="number" value={form.trial_days} onChange={(v) => update('trial_days', v)} />
          </div>
        </div>
      )}
      {form.benefit_type === 'discount_percent' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.formGroup}>
            <label style={s.label}>Desconto (%)</label>
            <Input type="number" value={form.discount_percent} onChange={(v) => update('discount_percent', v)} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Plano aplicável</label>
            <Select value={form.discount_plan} onChange={(v) => update('discount_plan', v)} options={[{ value: 'starter', label: 'Starter' }, { value: 'pro', label: 'Pro' }, { value: 'enterprise', label: 'Enterprise' }]} />
          </div>
        </div>
      )}
      {form.benefit_type === 'gift' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: '12px 16px', borderRadius: 10, background: T.purpleBg, border: '1px solid rgba(168,85,247,0.2)', fontSize: 12, color: T.purple }}>
            O presente fica pendente na conta do cliente. Ele precisa entrar no sistema e resgatar manualmente.
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Mensagem do presente</label>
            <Input value={form.gift_message} onChange={(v) => update('gift_message', v)} placeholder="Voce ganhou um presente especial!" />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Tipo do presente</label>
            <Select value={form.gift_type} onChange={(v) => update('gift_type', v)} options={[{ value: 'credits', label: 'Creditos ($)' }, { value: 'trial', label: 'Trial (dias gratis)' }, { value: 'discount_percent', label: 'Desconto %' }]} />
          </div>
          {form.gift_type === 'credits' && (
            <div style={s.formGroup}>
              <label style={s.label}>Valor em creditos ($)</label>
              <input style={s.input} type="number" step="0.01" value={form.gift_credits_amount} onInput={(e: any) => update('gift_credits_amount', e.target.value)} />
            </div>
          )}
          {form.gift_type === 'trial' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Plano</label>
                <Select value={form.gift_trial_plan} onChange={(v) => update('gift_trial_plan', v)} options={[{ value: 'starter', label: 'Starter' }, { value: 'pro', label: 'Pro' }, { value: 'enterprise', label: 'Enterprise' }]} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Dias de trial</label>
                <Input type="number" value={form.gift_trial_days} onChange={(v) => update('gift_trial_days', v)} />
              </div>
            </div>
          )}
          {form.gift_type === 'discount_percent' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Desconto (%)</label>
                <Input type="number" value={form.gift_discount_percent} onChange={(v) => update('gift_discount_percent', v)} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Plano aplicavel</label>
                <Select value={form.gift_discount_plan} onChange={(v) => update('gift_discount_plan', v)} options={[{ value: 'starter', label: 'Starter' }, { value: 'pro', label: 'Pro' }, { value: 'enterprise', label: 'Enterprise' }]} />
              </div>
            </div>
          )}
        </div>
      )}

      <div style={s.formGroup}>
        <label style={s.label}>Limite de resgates (vazio = ilimitado)</label>
        <Input type="number" value={form.max_redemptions} onChange={(v) => update('max_redemptions', v)} placeholder="Ilimitado" />
      </div>

      {/* Fonte de Tráfego */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 16 }}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Fonte de Tráfego</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.text, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={form.any_traffic_source}
              onChange={(e: any) => update('any_traffic_source', e.target.checked)}
              style={{ width: 18, height: 18, accentColor: T.primary }}
            />
            Qualquer fonte de tráfego
          </label>
        </div>
        {!form.any_traffic_source && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TRAFFIC_SOURCES.map(src => (
              <label key={src.id} style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                padding: '6px 12px', borderRadius: 8,
                background: form.allowed_sources.includes(src.id) ? `${SOURCE_COLORS[src.id]}20` : T.bgCard,
                border: `1px solid ${form.allowed_sources.includes(src.id) ? SOURCE_COLORS[src.id] : T.bgHover}`,
                color: form.allowed_sources.includes(src.id) ? SOURCE_COLORS[src.id] : T.textSecondary,
                fontSize: 13, fontWeight: 500, transition: 'all 0.15s',
              }}>
                <input
                  type="checkbox"
                  checked={form.allowed_sources.includes(src.id)}
                  onChange={() => toggleSource(src.id)}
                  style={{ width: 14, height: 14, accentColor: SOURCE_COLORS[src.id] }}
                />
                {src.label}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* ── SEÇÃO A: Banner Antes do Login ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, margin: 0 }}>📢 Banner Antes do Login</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.text, fontSize: 13 }}>
            <input type="checkbox" checked={!!form.banner_before_enabled} onChange={(e: any) => update('banner_before_enabled', e.target.checked ? 1 : 0)} style={{ width: 18, height: 18, accentColor: T.primary }} />
            Ativar
          </label>
        </div>
        <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12, marginTop: 0 }}>Aparece quando o visitante acessa o site com o link da promoção, antes de fazer login</p>
        {!!form.banner_before_enabled && (
          <>
            <TemplatePicker templates={BEFORE_LOGIN_TEMPLATES} selectedId={form.banner_before_template} codeValue={form.code} onSelect={(t: any) => {
              update('banner_before_template', t.id);
              update('banner_before_title', t.title);
              update('banner_before_message', t.message);
              update('banner_before_bg_color', t.bg_color);
            }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Título</label>
                <Input value={form.banner_before_title} onChange={(v) => update('banner_before_title', v)} placeholder="Seu código foi ativado!" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Cor de fundo</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.banner_before_bg_color} onInput={(e: any) => update('banner_before_bg_color', e.target.value)} style={{ width: 40, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                  <Input style={{ flex: 1 }} value={form.banner_before_bg_color} onChange={(v) => update('banner_before_bg_color', v)} />
                </div>
              </div>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Mensagem <span style={{ fontWeight: 400, color: T.textMuted }}>(use {'{{CODE}}'} para o código da campanha)</span></label>
              <Textarea style={s.textarea} value={form.banner_before_message} onInput={(e: any) => update('banner_before_message', e.target.value)} placeholder="Faça login para garantir seu benefício!" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Mídia (imagem/GIF/vídeo)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="file" accept="image/*,video/mp4,video/webm" id="banner-before-file" style={{ display: 'none' }} onChange={async (e: any) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 10 * 1024 * 1024) { setError('Arquivo excede 10MB'); return; }
                  setUploading(true); setError('');
                  try { const fd = new FormData(); fd.append('file', file); const res = await fetch('/api/v1/admin/promotions/upload-media', { method: 'POST', credentials: 'include', body: fd }); const json = await res.json() as any; if (!res.ok) throw new Error(json?.error?.message || 'Erro'); const url = json?.data?.url || json?.url; if (url) update('banner_before_image_url', url); else setError('Sem URL'); } catch (err: any) { setError(err?.message || 'Erro'); } finally { setUploading(false); e.target.value = ''; }
                }} />
                {!form.banner_before_image_url ? (
                  <Button type="button" variant="ghost" disabled={uploading} onClick={() => document.getElementById('banner-before-file')?.click()} style={{ justifyContent: 'center' }}>{uploading ? 'Enviando...' : '📎 Importar arquivo'}</Button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {isVideoUrl(form.banner_before_image_url) ? <video src={form.banner_before_image_url} style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8 }} autoPlay muted loop playsInline /> : <img src={form.banner_before_image_url} alt="" style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button type="button" variant="ghost" size="xs" onClick={() => document.getElementById('banner-before-file')?.click()} disabled={uploading}>{uploading ? '' : 'Trocar'}</Button>
                      <Button type="button" variant="danger" size="xs" onClick={() => update('banner_before_image_url', '')}>✕ Remover</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <BannerPreview title={resolveVars(form.banner_before_title, form.code)} message={resolveVars(form.banner_before_message, form.code)} bgColor={form.banner_before_bg_color} imageUrl={form.banner_before_image_url} maxRedemptions={form.max_redemptions ? parseInt(form.max_redemptions) : 0} currentRedemptions={promotion?.current_redemptions || 0} />
          </>
        )}
      </div>

      {/* ── SEÇÃO B: Banner Depois do Login ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, margin: 0 }}>Banner Depois do Login</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.text, fontSize: 13 }}>
            <input type="checkbox" checked={!!form.banner_after_enabled} onChange={(e: any) => update('banner_after_enabled', e.target.checked ? 1 : 0)} style={{ width: 18, height: 18, accentColor: T.success }} />
            Ativar
          </label>
        </div>
        <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12, marginTop: 0 }}>Aparece após o login, informando o que o usuário ganhou</p>
        {!!form.banner_after_enabled && (
          <>
            <TemplatePicker templates={AFTER_LOGIN_TEMPLATES} selectedId={form.banner_after_template} codeValue={form.code} onSelect={(t: any) => {
              update('banner_after_template', t.id);
              update('banner_after_title', t.title);
              update('banner_after_message', t.message);
              update('banner_after_bg_color', t.bg_color);
            }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Título</label>
                <Input value={form.banner_after_title} onChange={(v) => update('banner_after_title', v)} placeholder="Parabéns! Créditos adicionados!" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Cor de fundo</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.banner_after_bg_color} onInput={(e: any) => update('banner_after_bg_color', e.target.value)} style={{ width: 40, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                  <Input style={{ flex: 1 }} value={form.banner_after_bg_color} onChange={(v) => update('banner_after_bg_color', v)} />
                </div>
              </div>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Mensagem</label>
              <Textarea style={s.textarea} value={form.banner_after_message} onInput={(e: any) => update('banner_after_message', e.target.value)} placeholder="Seus créditos já estão disponíveis!" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Mídia (imagem/GIF/vídeo)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="file" accept="image/*,video/mp4,video/webm" id="banner-after-file" style={{ display: 'none' }} onChange={async (e: any) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 10 * 1024 * 1024) { setError('Arquivo excede 10MB'); return; }
                  setUploading(true); setError('');
                  try { const fd = new FormData(); fd.append('file', file); const res = await fetch('/api/v1/admin/promotions/upload-media', { method: 'POST', credentials: 'include', body: fd }); const json = await res.json() as any; if (!res.ok) throw new Error(json?.error?.message || 'Erro'); const url = json?.data?.url || json?.url; if (url) update('banner_after_image_url', url); else setError('Sem URL'); } catch (err: any) { setError(err?.message || 'Erro'); } finally { setUploading(false); e.target.value = ''; }
                }} />
                {!form.banner_after_image_url ? (
                  <Button type="button" variant="ghost" disabled={uploading} onClick={() => document.getElementById('banner-after-file')?.click()} style={{ justifyContent: 'center' }}>{uploading ? 'Enviando...' : '📎 Importar arquivo'}</Button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {isVideoUrl(form.banner_after_image_url) ? <video src={form.banner_after_image_url} style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8 }} autoPlay muted loop playsInline /> : <img src={form.banner_after_image_url} alt="" style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button type="button" variant="ghost" size="xs" onClick={() => document.getElementById('banner-after-file')?.click()} disabled={uploading}>{uploading ? '' : 'Trocar'}</Button>
                      <Button type="button" variant="danger" size="xs" onClick={() => update('banner_after_image_url', '')}>✕ Remover</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Exibir banner X vezes</label>
              <Input type="number" value={form.banner_show_count} onChange={(v) => update('banner_show_count', v)} />
            </div>
            <BannerPreview title={resolveVars(form.banner_after_title, form.code)} message={resolveVars(form.banner_after_message, form.code)} bgColor={form.banner_after_bg_color} imageUrl={form.banner_after_image_url} maxRedemptions={form.max_redemptions ? parseInt(form.max_redemptions) : 0} currentRedemptions={promotion?.current_redemptions || 0} />
          </>
        )}
      </div>

      {/* ── SEÇÃO C: Banner Promoção Encerrada ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, margin: 0 }}>⌛ Banner Promoção Encerrada</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.text, fontSize: 13 }}>
            <input type="checkbox" checked={!!form.banner_expired_enabled} onChange={(e: any) => update('banner_expired_enabled', e.target.checked ? 1 : 0)} style={{ width: 18, height: 18, accentColor: T.textMuted }} />
            Ativar
          </label>
        </div>
        <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12, marginTop: 0 }}>Aparece quando a promoção já passou da data de fim</p>
        {!!form.banner_expired_enabled && (
          <>
            <TemplatePicker templates={EXPIRED_TEMPLATES} selectedId={form.banner_expired_template} codeValue={form.code} onSelect={(t: any) => {
              update('banner_expired_template', t.id);
              update('banner_expired_title', t.title);
              update('banner_expired_message', t.message);
              update('banner_expired_bg_color', t.bg_color);
            }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Título</label>
                <Input value={form.banner_expired_title} onChange={(v) => update('banner_expired_title', v)} placeholder="⌛ Esta promoção já encerrou" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Cor de fundo</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.banner_expired_bg_color} onInput={(e: any) => update('banner_expired_bg_color', e.target.value)} style={{ width: 40, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                  <Input style={{ flex: 1 }} value={form.banner_expired_bg_color} onChange={(v) => update('banner_expired_bg_color', v)} />
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Mensagem</label>
                <Textarea style={s.textarea} value={form.banner_expired_message} onInput={(e: any) => update('banner_expired_message', e.target.value)} placeholder="Fique de olho para as próximas ofertas!" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Imagem / GIF / Vídeo</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input type="file" accept="image/*,video/mp4,video/webm" id="banner-expired-file" style={{ display: 'none' }} onChange={async (e: any) => {
                    const file = e.target.files?.[0]; if (!file) return; if (file.size > 10 * 1024 * 1024) { setError('Arquivo excede 10MB'); return; } setUploading(true); setError('');
                    try { const fd = new FormData(); fd.append('file', file); const res = await fetch('/api/v1/admin/promotions/upload-media', { method: 'POST', credentials: 'include', body: fd }); const json = await res.json() as any; if (!res.ok) throw new Error(json?.error?.message || 'Erro'); const url = json?.data?.url || json?.url; if (url) update('banner_expired_image_url', url); else setError('Sem URL'); } catch (err: any) { setError(err?.message || 'Erro'); } finally { setUploading(false); e.target.value = ''; }
                  }} />
                  {!form.banner_expired_image_url ? (
                    <Button type="button" variant="ghost" disabled={uploading} onClick={() => document.getElementById('banner-expired-file')?.click()} style={{ justifyContent: 'center' }}>{uploading ? 'Enviando...' : '📎 Importar arquivo'}</Button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>{isVideoUrl(form.banner_expired_image_url) ? <video src={form.banner_expired_image_url} style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8 }} autoPlay muted loop playsInline /> : <img src={form.banner_expired_image_url} alt="" style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button type="button" variant="ghost" size="xs" onClick={() => document.getElementById('banner-expired-file')?.click()} disabled={uploading}>{uploading ? '' : 'Trocar'}</Button>
                        <Button type="button" variant="danger" size="xs" onClick={() => update('banner_expired_image_url', '')}>✕ Remover</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <BannerPreview title={form.banner_expired_title} message={form.banner_expired_message} bgColor={form.banner_expired_bg_color} imageUrl={form.banner_expired_image_url} maxRedemptions={0} currentRedemptions={0} />
          </>
        )}
      </div>

      {/* Countdown (Pré-Lançamento) */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 16 }}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Countdown (Pré-Lançamento)</h4>
        <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
          Quando a promoção ainda não começou (starts_at no futuro), mostra um banner de contagem regressiva em vez do banner normal.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.text, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={!!form.countdown_enabled}
              onChange={(e: any) => update('countdown_enabled', e.target.checked ? 1 : 0)}
              style={{ width: 18, height: 18, accentColor: T.purple }}
            />
            Ativar countdown antes do início
          </label>
        </div>

        {!!form.countdown_enabled && (
          <>
            <TemplatePicker templates={COUNTDOWN_TEMPLATES} selectedId={form.countdown_template} codeValue={form.code} onSelect={(t: any) => {
              update('countdown_template', t.id);
              update('countdown_title', t.title);
              update('countdown_message', t.message);
              update('countdown_bg_color', t.bg_color);
              if (t.text_color) update('countdown_text_color', t.text_color);
            }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Título do countdown</label>
                <Input value={form.countdown_title} onChange={(v) => update('countdown_title', v)} placeholder="Promoção chegando!" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Cor de fundo</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.countdown_bg_color} onInput={(e: any) => update('countdown_bg_color', e.target.value)} style={{ width: 40, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                  <Input style={{ flex: 1 }} value={form.countdown_bg_color} onChange={(v) => update('countdown_bg_color', v)} />
                </div>
              </div>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Mensagem do countdown</label>
              <Textarea style={s.textarea} value={form.countdown_message} onInput={(e: any) => update('countdown_message', e.target.value)} placeholder="Prepare-se para uma oferta incrível!" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Cor do texto</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.countdown_text_color} onInput={(e: any) => update('countdown_text_color', e.target.value)} style={{ width: 40, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                  <Input style={{ flex: 1 }} value={form.countdown_text_color} onChange={(v) => update('countdown_text_color', v)} />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Mídia do countdown</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/webm"
                    id="countdown-file-input"
                    style={{ display: 'none' }}
                    onChange={async (e: any) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) { setError('Arquivo excede limite de 10MB'); return; }
                      setUploading(true); setError('');
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await api.post<any>('/admin/promotions/upload-media', fd as any, { raw: true });
                        const url = res?.data?.url || res?.url;
                        if (url) update('countdown_image_url', url);
                        else setError('Erro: resposta sem URL');
                      } catch (err: any) { setError(err?.message || 'Erro no upload'); }
                      finally { setUploading(false); e.target.value = ''; }
                    }}
                  />
                  {!form.countdown_image_url ? (
                    <Button type="button" disabled={uploading} onClick={() => document.getElementById('countdown-file-input')?.click()} variant="ghost" style={{ justifyContent: 'center' }}>
                      {uploading ? 'Enviando...' : '📎 Importar arquivo'}
                    </Button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {isVideoUrl(form.countdown_image_url)
                        ? <video src={form.countdown_image_url} style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8 }} autoPlay muted loop playsInline />
                        : <img src={form.countdown_image_url} alt="" style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />
                      }
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button type="button" variant="ghost" size="xs" onClick={() => document.getElementById('countdown-file-input')?.click()} disabled={uploading}>Trocar</Button>
                        <Button type="button" variant="danger" size="xs" onClick={() => update('countdown_image_url', '')}>✕ Remover</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Preview do countdown */}
            <CountdownPreview
              title={form.countdown_title}
              message={form.countdown_message}
              bgColor={form.countdown_bg_color}
              textColor={form.countdown_text_color}
              imageUrl={form.countdown_image_url}
              startsAt={form.starts_at}
              maxRedemptions={form.max_redemptions ? parseInt(form.max_redemptions) : 0}
              currentRedemptions={promotion?.current_redemptions || 0}
            />
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Salvando...' : (promotion ? 'Salvar' : 'Criar Campanha')}
        </Button>
      </div>
    </div>
  );
}

// ── Aba Analytics ──
function AnalyticsTab() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [selectedPromo, setSelectedPromo] = useState('');

  const fetchAnalytics = async (promoId?: string) => {
    setLoading(true);
    try {
      const query = promoId ? `?promotion_id=${promoId}` : '';
      const res = await api.get<any>(`/admin/promotions/analytics${query}`);
      console.log('[Analytics] response:', res);
      setData(res?.data || res || {});
    } catch (err: any) {
      console.error('[Analytics] erro:', err?.message || err);
    }
    setLoading(false);
  };

  const fetchPromotions = async () => {
    try {
      const res = await api.get<any>('/admin/promotions?limit=100');
      setPromotions(res?.data?.promotions || res?.promotions || []);
    } catch {}
  };

  useEffect(() => { fetchPromotions(); fetchAnalytics(); }, []);

  const handleFilter = (promoId: string) => {
    setSelectedPromo(promoId);
    fetchAnalytics(promoId || undefined);
  };

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;
  if (!data) return <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Erro ao carregar analytics</div>;

  const chartData = (data.visits_per_day || []).map((d: any) => ({
    label: d.date?.slice(5) || '',
    value: d.visits || 0,
  }));

  return (
    <div>
      {/* Filtro por campanha */}
      <div style={{ marginBottom: 20 }}>
        <Select style={{ maxWidth: 300 }} value={selectedPromo} onChange={(v) => handleFilter(v)} placeholder="Todas as campanhas" options={[{ value: '', label: 'Todas as campanhas' }, ...promotions.map((p: any) => ({ value: p.id, label: `${p.name} (${p.code})` }))]} />
      </div>

      {/* Cards de métricas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total de Visitas', value: data.total_visits || 0, color: T.primary },
          { label: 'Cadastros', value: data.total_signups || 0, color: T.purple },
          { label: 'Resgates', value: data.total_redemptions || 0, color: T.success },
          { label: 'Rejeitados', value: data.total_rejected || 0, color: T.danger },
          { label: 'Taxa de Conversão', value: `${data.conversion_rate || 0}%`, color: T.warning },
        ].map((m, i) => (
          <div key={i} style={{ ...s.card, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{m.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Gráfico de visitas por dia */}
      <div style={{ ...s.card, marginBottom: 24 }}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Visitas por Dia (Últimos 30 dias)</h4>
        <BarChart data={chartData} height={160} />
      </div>

      {/* Tabela: Por Fonte de Tráfego */}
      <div style={{ ...s.card, marginBottom: 24 }}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Por Fonte de Tráfego</h4>
        {(data.by_source || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>Sem dados de fontes</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Fonte</th>
                <th style={s.th}>Visitas</th>
                <th style={s.th}>Cadastros</th>
                <th style={s.th}>Resgates</th>
                <th style={s.th}>Rejeitados</th>
                <th style={s.th}>Conversão %</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_source || []).map((src: any) => {
                const conv = src.visits > 0 ? ((src.redemptions / src.visits) * 100).toFixed(1) : '0';
                const srcColor = SOURCE_COLORS[src.source] || T.textMuted;
                return (
                  <tr key={src.source}>
                    <td style={s.td}>
                      <span style={s.badge(srcColor)}>{SOURCE_LABELS[src.source] || src.source}</span>
                    </td>
                    <td style={s.td}>{src.visits}</td>
                    <td style={s.td}>{src.signups}</td>
                    <td style={s.td}>{src.redemptions}</td>
                    <td style={s.td}>{src.rejected}</td>
                    <td style={s.td}>{conv}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Tabela: Por Campanha */}
      <div style={{ ...s.card, marginBottom: 24 }}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Por Campanha</h4>
        {(data.by_campaign || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>Sem dados de campanhas</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Campanha</th>
                <th style={s.th}>Código</th>
                <th style={s.th}>Visitas</th>
                <th style={s.th}>Resgates</th>
                <th style={s.th}>Total Resgatado</th>
                <th style={s.th}>Conversão %</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_campaign || []).map((camp: any) => {
                const conv = camp.visits > 0 ? ((camp.redemptions / camp.visits) * 100).toFixed(1) : '0';
                return (
                  <tr key={camp.id}>
                    <td style={s.td}><span style={{ fontWeight: 600 }}>{camp.name}</span></td>
                    <td style={s.td}><code style={{ fontSize: 11, background: T.border, padding: '2px 6px', borderRadius: 4 }}>{camp.code}</code></td>
                    <td style={s.td}>{camp.visits}</td>
                    <td style={s.td}>{camp.redemptions}</td>
                    <td style={s.td}>{camp.total_redeemed}</td>
                    <td style={s.td}>{conv}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Tabela: Top Customers */}
      <div style={{ ...s.card, marginBottom: 24 }}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 16 }}>👑 Top Customers</h4>
        {(data.top_customers || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>Sem dados de clientes</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Email</th>
                <th style={s.th}>Promo Resgatada</th>
                <th style={s.th}>Total Gasto</th>
                <th style={s.th}>Data Cadastro</th>
                <th style={s.th}>Último Ativo</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_customers || []).map((cust: any, i: number) => (
                <tr key={i}>
                  <td style={s.td}>{cust.email || '-'}</td>
                  <td style={s.td}><code style={{ fontSize: 11 }}>{cust.redeemed_promo}</code></td>
                  <td style={s.td}>${parseFloat(cust.total_spent || 0).toFixed(2)}</td>
                  <td style={s.td}>{formatDate(cust.signed_up_at)}</td>
                  <td style={s.td}>{formatDate(cust.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tabela: Visitas Recentes */}
      <div style={s.card}>
        <h4 style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 16 }}>🕐 Visitas Recentes</h4>
        {(data.recent_visits || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>Sem visitas registradas</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Data</th>
                <th style={s.th}>Código</th>
                <th style={s.th}>Fonte</th>
                <th style={s.th}>Ação</th>
                <th style={s.th}>Customer</th>
                <th style={s.th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_visits || []).map((v: any) => {
                const actionColor = ACTION_COLORS[v.action] || T.textMuted;
                const srcColor = SOURCE_COLORS[v.utm_source] || SOURCE_COLORS['direct'];
                return (
                  <tr key={v.id}>
                    <td style={s.td}>{formatDate(v.created_at)}</td>
                    <td style={s.td}><code style={{ fontSize: 11 }}>{v.promo_code}</code></td>
                    <td style={s.td}>
                      <span style={s.badge(srcColor)}>{SOURCE_LABELS[v.utm_source] || v.utm_source || 'Direto'}</span>
                    </td>
                    <td style={s.td}>
                      <span style={s.badge(actionColor)}>{ACTION_LABELS[v.action] || v.action}</span>
                      {v.rejection_reason && <span style={{ fontSize: 10, color: T.danger, marginLeft: 4 }}>({v.rejection_reason})</span>}
                    </td>
                    <td style={s.td}>{v.customer_email || <span style={{ color: T.textMuted }}>Anônimo</span>}</td>
                    <td style={s.td}><span style={{ fontSize: 11, color: T.textMuted }}>{v.ip || '-'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──
// ── AffiliatesConfigTab — Configurações do sistema de afiliados ──
const AFF_TRAFFIC_SOURCES = ['google', 'instagram', 'facebook', 'tiktok', 'youtube', 'twitter', 'email', 'direct', 'other'];
const AFF_TRAFFIC_LABELS: Record<string, string> = {
  google: 'Google', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok',
  youtube: 'YouTube', twitter: 'Twitter/X', email: 'Email', direct: 'Direto', other: 'Outro',
};
const PLAN_OPTIONS = [
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

function AffiliatesConfigTab() {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/affiliates/settings');
      const data = await res.json() as any;
      if (data.success) setSettings(data.data.settings);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  const save = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      await api.put('/admin/affiliates/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {} finally { setSaving(false); }
  };

  const update = (key: string, val: any) => setSettings((prev: any) => ({ ...prev, [key]: val }));

  const getSelectedSources = (): string[] => {
    if (!settings?.allowed_traffic_sources || settings.allowed_traffic_sources === '*') return AFF_TRAFFIC_SOURCES;
    return settings.allowed_traffic_sources.split(',').filter(Boolean);
  };

  const toggleSource = (src: string) => {
    const current = getSelectedSources();
    const next = current.includes(src) ? current.filter(s2 => s2 !== src) : [...current, src];
    update('allowed_traffic_sources', next.length === AFF_TRAFFIC_SOURCES.length ? '*' : next.join(','));
  };

  if (loading) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;
  if (!settings) return <div style={{ padding: 40, textAlign: 'center', color: T.danger }}>Erro ao carregar configurações</div>;

  const selectedSources = getSelectedSources();

  return (
    <div>
      {/* Toggle principal */}
      <div style={{ ...s.card, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Sistema de Afiliados</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Ativar o programa de afiliados para clientes</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!settings.affiliate_system_enabled} onChange={(e) => update('affiliate_system_enabled', (e.target as HTMLInputElement).checked ? 1 : 0)} style={{ width: 18, height: 18, accentColor: T.primary }} />
          <span style={{ fontSize: 13, color: settings.affiliate_system_enabled ? T.success : T.textMuted, fontWeight: 600 }}>
            {settings.affiliate_system_enabled ? 'Ativo' : 'Inativo'}
          </span>
        </label>
      </div>

      {/* Limites de Campanha */}
      <div style={{ ...s.card, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>📏 Limites de Campanha</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          <div style={s.formGroup}>
            <label style={s.label}>Máx. Créditos ($)</label>
            <input type="number" style={s.input} value={settings.max_credits_amount || ''} min={0} step={0.5}
              onInput={(e) => update('max_credits_amount', parseFloat((e.target as HTMLInputElement).value) || 0)} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Máx. Dias de Trial</label>
            <input type="number" style={s.input} value={settings.max_trial_days || ''} min={0}
              onInput={(e) => update('max_trial_days', parseInt((e.target as HTMLInputElement).value) || 0)} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Máx. Plano Trial</label>
            <Select value={settings.max_trial_plan || 'pro'}
              onChange={(v) => update('max_trial_plan', v)}
              options={PLAN_OPTIONS} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Máx. Desconto (%)</label>
            <input type="number" style={s.input} value={settings.max_discount_percent || ''} min={0} max={100}
              onInput={(e) => update('max_discount_percent', parseInt((e.target as HTMLInputElement).value) || 0)} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Máx. Plano Desconto</label>
            <Select value={settings.max_discount_plan || 'pro'}
              onChange={(v) => update('max_discount_plan', v)}
              options={PLAN_OPTIONS} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Máx. Resgates/Campanha</label>
            <input type="number" style={s.input} value={settings.max_redemptions_per_campaign || ''} min={1}
              onInput={(e) => update('max_redemptions_per_campaign', parseInt((e.target as HTMLInputElement).value) || 0)} />
          </div>
        </div>
      </div>

      {/* Fontes de Tráfego */}
      <div style={{ ...s.card, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>Fontes de Tráfego Disponíveis</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {AFF_TRAFFIC_SOURCES.map(src => (
            <label key={src} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, background: selectedSources.includes(src) ? T.primaryBg : T.bgCard, border: `1px solid ${selectedSources.includes(src) ? T.primary : T.border}`, transition: 'all 0.15s' }}>
              <input type="checkbox" checked={selectedSources.includes(src)} onChange={() => toggleSource(src)} style={{ accentColor: T.primary }} />
              <span style={{ fontSize: 13, color: selectedSources.includes(src) ? T.primary : T.textSecondary }}>{AFF_TRAFFIC_LABELS[src]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Comissões Padrão */}
      <div style={{ ...s.card, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>Comissões Padrão</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.formGroup}>
            <label style={s.label}>Comissão LLM (%)</label>
            <input type="number" style={s.input} value={settings.default_commission_llm_percent || ''} min={0} max={100} step={0.5}
              onInput={(e) => update('default_commission_llm_percent', parseFloat((e.target as HTMLInputElement).value) || 0)} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Comissão Subscription (%)</label>
            <input type="number" style={s.input} value={settings.default_commission_subscription_percent || ''} min={0} max={100} step={0.5}
              onInput={(e) => update('default_commission_subscription_percent', parseFloat((e.target as HTMLInputElement).value) || 0)} />
          </div>
        </div>
      </div>

      {/* Aprovação */}
      <div style={{ ...s.card, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Requer Aprovação Manual</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Se desativado, afiliados são aprovados automaticamente</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!settings.require_approval} onChange={(e) => update('require_approval', (e.target as HTMLInputElement).checked ? 1 : 0)} style={{ width: 18, height: 18, accentColor: T.primary }} />
          <span style={{ fontSize: 13, color: settings.require_approval ? T.warning : T.success, fontWeight: 600 }}>
            {settings.require_approval ? 'Sim' : 'Automático'}
          </span>
        </label>
      </div>

      {/* Preview */}
      <div style={{ ...s.card, marginBottom: 20, background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginBottom: 8 }}>Preview</div>
        <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6 }}>
          Afiliado poderá criar campanhas com até <b style={{ color: T.success }}>${settings.max_credits_amount}</b> em créditos,{' '}
          <b style={{ color: T.primary }}>{settings.max_trial_days} dias</b> de trial {settings.max_trial_plan?.charAt(0).toUpperCase() + settings.max_trial_plan?.slice(1)},{' '}
          <b style={{ color: T.warning }}>{settings.max_discount_percent}%</b> de desconto,{' '}
          e máximo de <b style={{ color: T.text }}>{settings.max_redemptions_per_campaign}</b> resgates por campanha.
          <br />
          Comissão padrão: <b style={{ color: T.success }}>{settings.default_commission_llm_percent}%</b> sobre LLM e{' '}
          <b style={{ color: T.success }}>{settings.default_commission_subscription_percent}%</b> sobre assinaturas.
        </div>
      </div>

      {/* Botão Salvar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar Configurações'}
        </Button>
        {saved && <span style={{ fontSize: 12, color: T.success }}>Configurações salvas com sucesso!</span>}
      </div>
    </div>
  );
}

export function AdminPromotions() {
  const [tab, setTab] = useState('campaigns');
  const [promotions, setPromotions] = useState<any[]>([]);
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editPromo, setEditPromo] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [copied, setCopied] = useState('');

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await api.get<any>(`/admin/promotions?status=${statusFilter}&limit=100`);
      setPromotions(res?.data?.promotions || res?.promotions || []);
    } catch {}
    setLoading(false);
  };

  const fetchRedemptions = async () => {
    try {
      const res = await api.get<any>('/admin/promotions?limit=100');
      const promos = res?.data?.promotions || res?.promotions || [];
      const allRedemptions: any[] = [];
      for (const p of promos.slice(0, 20)) {
        try {
          const rRes = await api.get<any>(`/admin/promotions/${p.id}/redemptions?limit=50`);
          const reds = rRes?.data?.redemptions || rRes?.redemptions || [];
          for (const r of reds) {
            allRedemptions.push({ ...r, promo_name: p.name, promo_code: p.code });
          }
        } catch {}
      }
      allRedemptions.sort((a, b) => new Date(b.redeemed_at).getTime() - new Date(a.redeemed_at).getTime());
      setRedemptions(allRedemptions);
    } catch {}
  };

  useEffect(() => { fetchPromotions(); }, [statusFilter]);
  useEffect(() => { if (tab === 'redemptions') fetchRedemptions(); }, [tab]);

  const handleToggle = async (id: string) => {
    try {
      await api.post(`/admin/promotions/${id}/toggle`, {});
      fetchPromotions();
    } catch {}
  };

  const handleArchive = async (id: string) => {
    if (!confirm('Arquivar esta campanha?')) return;
    try {
      await api.delete(`/admin/promotions/${id}`);
      fetchPromotions();
    } catch {}
  };

  const copyLink = (code: string) => {
    navigator.clipboard.writeText(`/?promo=${code}`);
    setCopied(code);
    setTimeout(() => setCopied(''), 2000);
  };

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={s.page}>
      <h1 style={s.title}>Campanhas Promocionais</h1>

      <div style={s.tabBar}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => { setTab(t.id); setEditPromo(null); }} style={s.tab(tab === t.id)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* ── ABA: Campanhas ── */}
      {tab === 'campaigns' && !editPromo && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['all', 'active', 'paused', 'expired', 'archived'].map(st => (
                <Button key={st} variant={statusFilter === st ? 'primary' : 'ghost'} size="xs" onClick={() => setStatusFilter(st)}>
                  {st === 'all' ? 'Todas' : STATUS_LABELS[st] || st}
                </Button>
              ))}
            </div>
            <Button variant="primary" onClick={() => setTab('create')}>
              {Icons.plus} Nova Campanha
            </Button>
          </div>

          {loading ? (
            <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
          ) : promotions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}></div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Nenhuma campanha encontrada</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Crie a primeira campanha promocional</div>
            </div>
          ) : (
            <div style={s.card}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Nome</th>
                    <th style={s.th}>Código</th>
                    <th style={s.th}>Tipo</th>
                    <th style={s.th}>Validade</th>
                    <th style={s.th}>Usos</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {promotions.map((p: any) => (
                    <tr key={p.id}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        {p.description && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{p.description.slice(0, 50)}</div>}
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <code style={{ background: T.border, padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, color: T.text }}>
                            {p.code}
                          </code>
                          <Button variant="ghost" size="xs" onClick={() => copyLink(p.code)} title="Copiar link" style={{ color: copied === p.code ? T.success : T.textMuted, padding: 2 }}>
                            {Icons.copy}
                          </Button>
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={s.badge(TYPE_COLORS[p.benefit_type] || T.textMuted)}>
                          {TYPE_LABELS[p.benefit_type] || p.benefit_type}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={{ fontSize: 12 }}>
                          <div>{formatDate(p.starts_at)}</div>
                          <div style={{ color: T.textMuted }}>até {formatDate(p.ends_at)}</div>
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{ fontWeight: 600 }}>{p.current_redemptions}</span>
                        <span style={{ color: T.textMuted }}>/{p.max_redemptions || '∞'}</span>
                      </td>
                      <td style={s.td}>
                        <span style={s.badge(STATUS_COLORS[p.status] || T.textMuted)}>
                          {STATUS_LABELS[p.status] || p.status}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button variant="ghost" size="xs" onClick={() => { setEditPromo(p); setTab('campaigns'); }} title="Editar">{Icons.edit}</Button>
                          <Button variant="ghost" size="xs" onClick={() => handleToggle(p.id)} title={p.status === 'active' ? 'Pausar' : 'Ativar'}>
                            {p.status === 'active' ? Icons.pause : Icons.play}
                          </Button>
                          <Button variant="ghost" size="xs" onClick={() => handleArchive(p.id)} title="Arquivar" style={{ color: T.danger }}>{Icons.trash}</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ABA: Editar (inline quando editPromo está setado) ── */}
      {tab === 'campaigns' && editPromo && (
        <CampaignForm promotion={editPromo} onSave={() => { setEditPromo(null); fetchPromotions(); }} onCancel={() => setEditPromo(null)} />
      )}

      {/* ── ABA: Criar Campanha ── */}
      {tab === 'create' && (
        <CampaignForm onSave={() => { setTab('campaigns'); fetchPromotions(); }} onCancel={() => setTab('campaigns')} />
      )}

      {/* ── ABA: Resgates ── */}
      {tab === 'redemptions' && (
        <div style={s.card}>
          <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Histórico de Resgates</h3>
          {redemptions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Nenhum resgate encontrado</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Customer</th>
                  <th style={s.th}>Campanha</th>
                  <th style={s.th}>Código</th>
                  <th style={s.th}>Data</th>
                  <th style={s.th}>Benefício</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r: any) => {
                  let benefit = '';
                  try {
                    const ba = JSON.parse(r.benefit_applied || '{}');
                    if (ba.type === 'credits') benefit = `$${ba.amount} créditos`;
                    else if (ba.type === 'trial') benefit = `Trial ${ba.days}d ${ba.plan}`;
                    else if (ba.type === 'discount_percent') benefit = `${ba.percent}% off ${ba.plan}`;
                  } catch {}
                  return (
                    <tr key={r.id}>
                      <td style={s.td}><code style={{ fontSize: 11 }}>{r.customer_id?.slice(0, 12)}...</code></td>
                      <td style={s.td}>{r.promo_name || '-'}</td>
                      <td style={s.td}><code style={{ fontSize: 11 }}>{r.promo_code || '-'}</code></td>
                      <td style={s.td}>{formatDate(r.redeemed_at)}</td>
                      <td style={s.td}>{benefit || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ABA: Analytics ── */}
      {tab === 'analytics' && <AnalyticsTab />}

      {/* ── ABA: Afiliados Config ── */}
      {tab === 'affiliates' && <AffiliatesConfigTab />}
    </div>
  );
}
